import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coffee, Croissant, Sparkles, Info, Leaf, Wheat, Salad, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

// Fixed curated presentation packages (no prices — prices are set by each locale and B&B)
const PACKAGES = [
  {
    key: "classica", icon: Coffee, name: "Colazione Classica",
    desc: "La colazione all'italiana, semplice e sempre perfetta.",
    products: ["Caffè oppure cappuccino", "Cornetto classico"],
  },
  {
    key: "speciale", icon: Croissant, name: "Colazione Speciale", featured: true,
    desc: "Un tocco in più per iniziare la giornata con gusto.",
    products: ["Cappuccino", "Cornetto farcito", "Succo di frutta"],
  },
  {
    key: "gourmet", icon: Sparkles, name: "Colazione Gourmet",
    desc: "L'esperienza completa, con pasticceria artigianale.",
    products: ["Colazione di pasticceria artigianale", "Bevanda calda", "Spremuta"],
  },
];

const CUSTOM = [
  { icon: Leaf, name: "Colazione Vegana" },
  { icon: Wheat, name: "Colazione Senza Glutine" },
  { icon: Salad, name: "Colazione Fit" },
  { icon: Coffee, name: "Colazione Salata" },
  { icon: Sun, name: "Colazione Siciliana" },
  { icon: Croissant, name: "Colazione Napoletana" },
];

export default function Pacchetti() {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [_, setPing] = useState(null);
  useEffect(() => { api.get("/packages/public").then(({ data }) => setPing(data)).catch(() => {}); }, []);

  return (
    <div>
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-orange">Pacchetti</span>
          <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-navy tracking-tight">Colazioni per ogni gusto</h1>
          <p className="mt-4 text-lg text-navy/70 max-w-2xl leading-relaxed">
            Tre proposte base che ogni locale può personalizzare e valorizzare con la propria identità.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((p) => (
            <div key={p.key} className={`rounded-3xl p-8 border transition-shadow hover:shadow-lg ${p.featured ? "bg-navy text-white border-navy" : "bg-white border-navy/10 text-navy"}`} data-testid={`package-${p.key}`}>
              {p.featured && <Badge className="bg-orange text-white mb-3">Più scelto</Badge>}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${p.featured ? "bg-white/10" : "bg-cream"}`}>
                <p.icon className="w-6 h-6 text-orange" />
              </div>
              <h3 className={`mt-4 text-2xl font-bold ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
              <p className={`mt-1 text-sm ${p.featured ? "text-white/60" : "text-navy/50"}`}>{p.desc}</p>
              <ul className="mt-6 space-y-2">
                {p.products.map((prod) => (
                  <li key={prod} className={`flex items-center gap-2 text-sm ${p.featured ? "text-white/80" : "text-navy/70"}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-orange shrink-0" /> {prod}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-start gap-3 text-sm text-navy/70 bg-cream rounded-2xl p-5">
          <Info className="w-5 h-5 text-orange shrink-0 mt-0.5" />
          <p>I prezzi vengono stabiliti dal locale convenzionato e dal B&B partner. Ogni bar o pasticceria inserisce i propri prodotti, prezzi, foto, orari e modalità di consegna; ogni B&B decide il prezzo finale applicato ai propri ospiti o lo include nella tariffa della camera.</p>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl lg:text-3xl font-bold text-navy">Ogni locale valorizza la propria identità</h2>
          <p className="mt-2 text-navy/60 max-w-2xl">MorningBox non impone un catalogo rigido: i locali possono creare le proprie varianti di colazione.</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CUSTOM.map((e) => (
              <div key={e.name} className="bg-white border border-navy/10 rounded-2xl p-5 flex items-center gap-3 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-cream flex items-center justify-center shrink-0"><e.icon className="w-5 h-5 text-orange" /></div>
                <p className="font-semibold text-navy">{e.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Button onClick={() => navigate("/registra/locale")} className="rounded-full bg-navy text-white h-12 px-6">Crea i tuoi pacchetti</Button>
          <Button onClick={() => navigate("/come-funziona")} variant="outline" className="rounded-full h-12 px-6">Come funziona</Button>
        </div>
      </section>
    </div>
  );
}
